import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../config";

const Reports = () => {
  const [farmData, setFarmData] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchResult, setSearchResult] = useState(null);

  const [openMonth, setOpenMonth] = useState(null); // open month
  const [openStatus, setOpenStatus] = useState(null); // open status
  const [openFarmer, setOpenFarmer] = useState(null); // open farmer
 

  const convertImage = (imageData) => {
    if (!imageData) return null;
    const base64String = btoa(String.fromCharCode(...imageData));
    return `data:image/jpeg;base64,${base64String}`;
  };

  // LOAD SAME DATA USED IN MAP
  useEffect(() => {
    axios
      .get(`${BASE_URL}/lisa_survey_irrigation/get_all`)
      .then((res) => {
        if (res.data.status && Array.isArray(res.data.data)) {
          // Sort alphabetically for binary search
          const sorted = res.data.data.sort((a, b) =>
            (a.farmers_name || "").localeCompare(b.farmers_name || "")
          );
          setFarmData(sorted);
        }
      })
      .catch((err) => console.error("Error fetching data:", err));
  }, []);

  // ============================
  // LOGARITHMIC LOOP — BINARY SEARCH
  // ============================
  const binarySearch = (name) => {
    let low = 0;
    let high = farmData.length - 1;
    const target = (name || "").toLowerCase().trim();

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const midName = (farmData[mid].farmers_name || "").toLowerCase();

      if (midName === target) return farmData[mid];
      if (midName < target) low = mid + 1;
      else high = mid - 1;
    }
    return null;
  };

  const handleSearch = () => {
    const result = binarySearch(searchInput);
    setSearchResult(result);
  };

  // ============================
  // GROUP BY MONTH → STATUS → FARMER
  // ============================
  const formatMonth = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  const groupedByMonth = farmData.reduce((group, item) => {
    const month = formatMonth(item.transdate || new Date());
    if (!group[month]) group[month] = {};

    const status = item.status || "Unknown";
    if (!group[month][status]) group[month][status] = {};

    const farmer = item.farmers_name || "Unnamed Farmer";
    if (!group[month][status][farmer]) group[month][status][farmer] = [];

    group[month][status][farmer].push(item);
    return group;
  }, {});

  // ICONS FOR STATUS CARDS
  const statusIcons = {
    Dry: "🔥",
    Wet: "💧",
    Unknown: "❔",
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 900 }}>
      <h1 className="fw-bold mb-4">📊 Reports Dashboard</h1>

      {/* ============================
          SEARCH — BINARY SEARCH
      ============================ */}
      <div className="card p-3 shadow-sm">
        <h3>🔍 Search Farmer</h3>

        <input
          className="form-control mt-2"
          placeholder="Enter Farmer Name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <button
          className="btn mt-2"
          style={{ backgroundColor: "#4CAF50", color: "white" }}
          onClick={handleSearch}
          type="button"
        >
          Search
        </button>

        <div className="mt-3">
          {searchResult ? (
            <div className="alert alert-success">
              <strong>{searchResult.farmers_name}</strong> found! <br />
              Status: {searchResult.status}
            </div>
          ) : searchInput ? (
            <div className="text-danger">No farmer found.</div>
          ) : null}
        </div>
      </div>

      {/* ============================
          GROUP BY MONTH → STATUS → FARMER → FARMS
      ============================ */}
      <h3 className="mt-5 mb-3">📅 Reports by Month</h3>

      <div>
        {Object.entries(groupedByMonth).map(([month, statuses]) => (
          <div key={month} className="mb-4">
            {/* MONTH CARD */}
            <div
              className="card shadow-sm p-3"
              style={{
                cursor: "pointer",
                borderRadius: 15,
                transition: "0.2s",
                background: "#f0f4ff",
                borderLeft: "5px solid #4CAF50",
              }}
              onClick={() => {
                setOpenMonth(openMonth === month ? null : month);
                setOpenStatus(null); // reset status
                setOpenFarmer(null); // reset farmer
              }}
            >
              <div style={{ fontSize: 24, fontWeight: "bold" }}>📅 {month}</div>
              <p className="text-muted mb-0">
                {Object.keys(statuses).reduce((count, status) => {
                  return count + Object.keys(statuses[status]).length;
                }, 0)} farmers
              </p>
            </div>

            {/* STATUSES INSIDE MONTH */}
            {openMonth === month && (
              <div className="mt-2" style={{ marginLeft: "20px", borderLeft: "2px solid #4CAF50", paddingLeft: "15px" }}>
                <div className="row">
                  {Object.entries(statuses).map(([status, farmers]) => (
                    <div key={status} className="col-md-6 mb-3">
                      {/* STATUS CARD */}
                      <div
                        className="card shadow-sm p-3 text-center"
                        style={{
                          cursor: "pointer",
                          borderRadius: 15,
                          transition: "0.2s",
                          background: "#f8f9fa",
                        }}
                        onClick={() => {
                          setOpenStatus(openStatus === status ? null : status);
                          setOpenFarmer(null); // reset farmer
                        }}
                      >
                        <div style={{ fontSize: 40 }}>{statusIcons[status] || "📍"}</div>

                        <h4 className="mt-2">{status}</h4>
                        <p className="text-muted">{Object.keys(farmers).length} farmers</p>
                      </div>

                      {/* FARMERS INSIDE STATUS */}
                      {openStatus === status && (
                        <div
                          className="card mt-2 p-3 shadow-sm"
                          style={{ maxHeight: "500px", overflowY: "auto" }}
                        >
                          <h5 className="fw-bold">{status} — Farmers</h5>

                          {Object.entries(farmers).map(([farmer, farmList]) => (
                            <div key={farmer} className="mb-2">
                              {/* FARMER NAME */}
                              <div
                                className="p-2 border rounded"
                                style={{ cursor: "pointer", background: "#eef2f7" }}
                                onClick={() =>
                                  setOpenFarmer(openFarmer === farmer ? null : farmer)
                                }
                              >
                                <strong>{farmer}</strong>
                              </div>

                              {/* FARMS UNDER FARMER */}
                              {openFarmer === farmer && (
                                <div className="mt-2 ps-3">
                                  {farmList.map((farm, idx) => {
                                    const imageSrc = farm.image?.data
                                      ? convertImage(farm.image.data)
                                      : null;

                                    return (
                                      <div key={idx} className="py-3 border-bottom">
                                        <small className="text-muted d-block mb-2">
                                          <strong>Farm #{idx + 1}</strong> <br />
                                          Longitude: {farm.longitude} | Latitude:{" "}
                                          {farm.latitude}
                                          <br />
                                          <em>{new Date(farm.transdate).toLocaleDateString()}</em>
                                        </small>

                                        {/* IMAGE DISPLAY */}
                                        {imageSrc && (
                                          <img
                                            src={imageSrc}
                                            alt="Farm"
                                            style={{
                                              width: "100%",
                                              maxWidth: "300px",
                                              height: "300px",
                                              objectFit: "cover",
                                              borderRadius: "8px",
                                              border: "1px solid #ddd",
                                            }}
                                          />
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Reports;